// UPost - Comprehensive Pricing Comparison Page
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Check, X, Users, Shield, Crown } from 'lucide-react';
import { SUBSCRIPTION_PRICES } from '../config';
import { Logo } from '../components/Logo';

export function PricingComparison() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSelectPlan = (tier) => {
    if (!user) {
      navigate('/register');
    } else if (tier === 'free') {
      navigate('/dashboard');
    } else {
      navigate('/upgrade');
    }
  };

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      icon: '📝',
      tagline: 'Perfect for getting started',
      price: 0,
      period: 'forever',
      badge: null,
      features: [
        { name: 'AI Words per Month', value: '18,400', included: true },
        { name: 'Novel Editor', value: true, included: true },
        { name: 'Screenplay Editor', value: false, included: false },
        { name: 'Auto-Save', value: true, included: true },
        { name: 'Version History', value: false, included: false },
        { name: 'Collaborator Seats', value: '0', included: false },
        { name: 'Priority AI Processing', value: false, included: false },
        { name: 'Export to Word', value: true, included: true },
        { name: 'Export to PDF', value: false, included: false },
        { name: 'Simple Timestamp', value: true, included: true },
        { name: 'Blockchain Certification', value: 'Add-on $9.99', included: false },
      ],
      cta: 'Get Started Free',
      highlight: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: '⚡',
      tagline: 'For serious writers',
      price: SUBSCRIPTION_PRICES.pro_monthly,
      period: 'month',
      yearlyPrice: SUBSCRIPTION_PRICES.pro_yearly,
      badge: 'POPULAR',
      features: [
        { name: 'AI Words per Month', value: 'Unlimited', included: true },
        { name: 'Novel Editor', value: true, included: true },
        { name: 'Screenplay Editor', value: true, included: true },
        { name: 'Auto-Save', value: true, included: true },
        { name: 'Version History', value: true, included: true },
        { name: 'Collaborator Seats', value: '0', included: false },
        { name: 'Priority AI Processing', value: true, included: true },
        { name: 'Export to Word', value: true, included: true },
        { name: 'Export to PDF', value: true, included: true },
        { name: 'No Branding on Exports', value: true, included: true },
        { name: 'Blockchain Certification', value: 'Add-on $9.99', included: false },
      ],
      cta: 'Upgrade to Pro',
      highlight: true,
    },
    {
      id: 'team',
      name: 'Team',
      icon: <Users className="w-6 h-6" />,
      tagline: 'Collaborate with your team',
      price: SUBSCRIPTION_PRICES.team_monthly,
      period: 'month',
      yearlyPrice: SUBSCRIPTION_PRICES.team_yearly,
      badge: 'COLLABORATE',
      features: [
        { name: 'AI Words per Month', value: 'Unlimited', included: true },
        { name: 'Novel Editor', value: true, included: true },
        { name: 'Screenplay Editor', value: true, included: true },
        { name: 'Auto-Save', value: true, included: true },
        { name: 'Version History', value: true, included: true },
        { name: 'Collaborator Seats', value: '3 seats', included: true },
        { name: 'Priority AI Processing', value: true, included: true },
        { name: 'Export to Word', value: true, included: true },
        { name: 'Export to PDF', value: true, included: true },
        { name: 'No Branding on Exports', value: true, included: true },
        { name: 'Real-time Collaboration', value: true, included: true },
        { name: 'Comment & Editing Roles', value: true, included: true },
        { name: 'Blockchain Certification', value: 'Add-on $9.99', included: false },
      ],
      cta: 'Upgrade to Team',
      highlight: false,
    },
    {
      id: 'admin',
      name: 'Admin',
      icon: <Crown className="w-6 h-6" />,
      tagline: 'For professional teams',
      price: SUBSCRIPTION_PRICES.admin_monthly,
      period: 'month',
      yearlyPrice: SUBSCRIPTION_PRICES.admin_yearly,
      badge: 'BEST VALUE',
      features: [
        { name: 'AI Words per Month', value: 'Unlimited', included: true },
        { name: 'Novel Editor', value: true, included: true },
        { name: 'Screenplay Editor', value: true, included: true },
        { name: 'Auto-Save', value: true, included: true },
        { name: 'Version History', value: true, included: true },
        { name: 'Collaborator Seats', value: '10 seats', included: true },
        { name: 'Priority AI Processing', value: true, included: true },
        { name: 'Export to Word', value: true, included: true },
        { name: 'Export to PDF', value: true, included: true },
        { name: 'No Branding on Exports', value: true, included: true },
        { name: 'Real-time Collaboration', value: true, included: true },
        { name: 'Comment & Editing Roles', value: true, included: true },
        { name: 'Admin Analytics', value: true, included: true },
        { name: 'Team Management Dashboard', value: true, included: true },
        { name: 'Blockchain Certification', value: 'Add-on $9.99', included: false },
      ],
      cta: 'Upgrade to Admin',
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <nav className="container mx-auto px-4 py-6 flex justify-between items-center">
        <Link to="/">
          <Logo variant="full" size="lg" />
        </Link>
        <div className="space-x-4">
          {user ? (
            <Link to="/dashboard">
              <Button>Go to Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost">Log In</Button>
              </Link>
              <Link to="/register">
                <Button>Sign Up</Button>
              </Link>
            </>
          )}
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
            Choose Your Perfect Plan
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Start free and upgrade anytime. All plans include our AI writing assistant and copyright protection features.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tiers.map((tier) => (
            <Card
              key={tier.id}
              className={`relative backdrop-blur-sm bg-white/90 ${
                tier.highlight ? 'border-2 border-purple-500 shadow-lg' : ''
              }`}
            >
              {tier.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-xs font-semibold">
                  {tier.badge}
                </div>
              )}
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  {typeof tier.icon === 'string' ? (
                    <span className="text-2xl">{tier.icon}</span>
                  ) : (
                    tier.icon
                  )}
                  <CardTitle>{tier.name}</CardTitle>
                </div>
                <CardDescription>{tier.tagline}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="text-4xl font-bold">
                    ${tier.price}
                    <span className="text-base font-normal text-gray-600">/{tier.period}</span>
                  </div>
                  {tier.yearlyPrice && (
                    <p className="text-sm text-green-600 mt-1">
                      ${tier.yearlyPrice}/year (save 20%)
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6 min-h-[320px]">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      {feature.included ? (
                        <Check className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                      ) : (
                        <X className="text-gray-300 flex-shrink-0 mt-0.5" size={16} />
                      )}
                      <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                        <strong>{feature.name}:</strong>{' '}
                        {typeof feature.value === 'boolean' ? '' : feature.value}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(tier.id)}
                  className={`w-full ${
                    tier.highlight
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : tier.id === 'free'
                      ? 'bg-gray-600 hover:bg-gray-700'
                      : ''
                  }`}
                >
                  {tier.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add-on Section */}
        <div className="max-w-3xl mx-auto mb-12">
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="text-blue-600" />
                Add-Ons & Extras
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold">Additional Collaborator Seat</h4>
                    <p className="text-sm text-gray-600">
                      Need more team members? Add extra seats to Team or Admin plans
                    </p>
                  </div>
                  <div className="text-2xl font-bold">${SUBSCRIPTION_PRICES.additional_seat}/month</div>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold">Blockchain Copyright Certification</h4>
                    <p className="text-sm text-gray-600">
                      Get immutable, court-admissible proof of copyright ownership
                    </p>
                  </div>
                  <div className="text-2xl font-bold">${SUBSCRIPTION_PRICES.copyright_blockchain}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Can I change plans anytime?</h3>
                <p className="text-sm text-gray-600">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate the billing.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">What are collaborator seats?</h3>
                <p className="text-sm text-gray-600">
                  Collaborator seats let you invite team members to view, comment on, or edit your documents in real-time. Available on Team and Admin plans.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Is there a free trial?</h3>
                <p className="text-sm text-gray-600">
                  Our Free plan includes 18,400 AI words per month with no time limit. It's perfect for testing the platform before upgrading.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-16">
          <Link to={user ? '/dashboard' : '/register'}>
            <Button size="lg" className="text-lg px-8 py-6">
              {user ? 'Go to Dashboard' : 'Start Writing Free →'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
